//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { IModerationItem, IModerationItemStatusData, MODERATION_STATUS_COLORS } from '@components/moderation-grid/moderationGridModel';
import { ITextInputValidation } from '@components/text-input/textInputModel';
import { IProduct } from '@models/IProduct';
import { isDefined } from '@utils/filterPredicates';
import { isValidUrlWithProtocol } from '@utils/urlValidation';
import { DYNAMIC_LANDING_PAGE_READABLE_NAME, DYNAMIC_LANDING_PAGE_URL_MAX_LENGTH, HEADLINE_MAX_LENGTH, PRODUCT_ERROR_STRINGS, STATIC_LANDING_PAGE_READABLE_NAME, STATIC_LANDING_PAGE_URL_MAX_LENGTH } from './constants/taggingConstants';
import { ITaggingFeedItem, LandingPageType, TaggingFeedType } from './models/taggingModels';

export function getTaggingModerationStatusData(item: IModerationItem): IModerationItemStatusData {
  const { itemType, publishedTimestamp } = item.moderationMetadata;
  const needsProducts = !item.products || item.products.length === 0;
  const isDeleted = itemType === TaggingFeedType.Trash;
  const pendingPublish = !isDefined(publishedTimestamp);
  const numberOfProductsWithErrors = getNumberOfProductsWithErrors(item.products);
  const allProductsHaveErrors = numberOfProductsWithErrors === item.products.length;

  if (allProductsHaveErrors) {
    return {
      text: 'Action Required',
      color: MODERATION_STATUS_COLORS.RED,
    };
  } else if (isDeleted) {
    return {
      text: 'Deleted',
      color: MODERATION_STATUS_COLORS.RED,
    };
  } else if (needsProducts) {
    return {
      text: 'Pending Products',
      color: MODERATION_STATUS_COLORS.YELLOW,
    };
  } else if (pendingPublish) {
    return {
      text: 'Pending Review',
      color: MODERATION_STATUS_COLORS.YELLOW,
    };
  } else {
    return {
      text: 'Published',
      color: MODERATION_STATUS_COLORS.GREEN,
    };
  }
}

export const validateHeadline = (headline: string): ITextInputValidation => {
  if (headline && headline.length > HEADLINE_MAX_LENGTH) {
    return {
      isValid: false,
      reason: `Error: Headline exceeds ${HEADLINE_MAX_LENGTH} character limit`,
    };
  } else {
    return {
      isValid: true,
    };
  }
};

export const validateLandingPageUrl = (
  landingPageType: LandingPageType,
  dynamicLandingPageUrl: string,
  staticLandingPageUrl: string,
): ITextInputValidation => {
  const url = getLandingPageUrl(landingPageType, dynamicLandingPageUrl, staticLandingPageUrl);
  const urlMaxLength =  getLandingPageUrlMaxLength(landingPageType);
  const landingPageTypeReadableName = getReadableLandingPageTypeName(landingPageType);

  if (!url) {
    return {
      isValid: false,
      reason: 'You must define a landing page url',
    };
  } else if (url.length > urlMaxLength) {
    return {
      isValid: false,
      reason: `${landingPageTypeReadableName}s have a max length of ${urlMaxLength}`,
    };
  } else if (!isValidUrlWithProtocol(url)) {
    return {
      isValid: false,
      reason: `You must provide a valid url, e.g. http://example.com`,
    };
  } else {
    return {
      isValid: true,
    };
  }
};

const getLandingPageUrlMaxLength: (landingPageType: LandingPageType) => number = (landingPageType) => {
  return landingPageType === LandingPageType.DynamicLandingPage
    ? DYNAMIC_LANDING_PAGE_URL_MAX_LENGTH
    : STATIC_LANDING_PAGE_URL_MAX_LENGTH;
};

const getReadableLandingPageTypeName: (landingPageType: LandingPageType) => string = (landingPageType) => {
  return landingPageType === LandingPageType.DynamicLandingPage
    ? DYNAMIC_LANDING_PAGE_READABLE_NAME
    : STATIC_LANDING_PAGE_READABLE_NAME;
};

const getLandingPageUrl: (
  landingPageType: LandingPageType,
  dynamicLandingPageUrl: string,
  staticLandingPageUrl: string,
) => string = (landingPageType, dynamicLandingPageUrl, staticLandingPageUrl) => {
  const isStaticLandingPage = landingPageType && landingPageType === LandingPageType.StaticLandingPage;
  const isDynamicLandingPage = landingPageType && landingPageType === LandingPageType.DynamicLandingPage;
  if (isStaticLandingPage) {
    return staticLandingPageUrl;
  } else if (isDynamicLandingPage) {
    return dynamicLandingPageUrl;
  } else {
    return null;
  }
};

export const getProductListErrorSummary: (products: IProduct[]) => string = (products) => {
  const numberOfProductsWithErrors = getNumberOfProductsWithErrors(products);
  if (!products) {
    return '';
  } else if (numberOfProductsWithErrors === products.length) {
    return PRODUCT_ERROR_STRINGS.SUMMARY.ALL;
  } else if (numberOfProductsWithErrors > 0) {
    return PRODUCT_ERROR_STRINGS.SUMMARY.MULTIPLE;
  } else {
    return '';
  }
};

const getNumberOfProductsWithErrors: (products: IProduct[]) => number = (products) => {
  if (!products || products.length === 0) {
    return undefined;
  } else {
    const productsWithErrors = products.filter((product) => getProductErrors(product).length > 0);
    return productsWithErrors.length;
  }
};

export const getProductErrorSummary: (product: IProduct) => string = (product) => {
  const errors = getProductErrors(product);
  if (errors.length > 0) {
    return PRODUCT_ERROR_STRINGS.SUMMARY.SINGLE;
  } else {
    return '';
  }
};

export const getProductErrors: (product: IProduct) => string[] = (product) => {
  const errors = [];

  if (product.isCrawled) {
    errors.push(PRODUCT_ERROR_STRINGS.INDIVIDUAL.CRAWLED);
  }

  if (product.isExpired) {
    errors.push(PRODUCT_ERROR_STRINGS.INDIVIDUAL.EXPIRED);
  } else if (!product.isAvailable) {
    errors.push(PRODUCT_ERROR_STRINGS.INDIVIDUAL.UNAVAILABLE);
  }

  if (!product.hasSpatialTags) {
    errors.push(PRODUCT_ERROR_STRINGS.INDIVIDUAL.SPATIALTAGS);
  }
  return errors;
};

// Certain pieces of item-level metadata are not returned by our APIs, but are
// still deterministic given context. This function can be used to fill-in missing
// pieces of metadata when need be.
export const shimItemType = (items: ITaggingFeedItem[], feedType: TaggingFeedType): ITaggingFeedItem[] => {
  const itemsWithShimmedType: ITaggingFeedItem[] = items.map((item) => {
    return {
      ...item,
      moderationMetadata: {
        ...item.moderationMetadata,
        itemType: feedType,
      },
    };
  });

  return itemsWithShimmedType;
};
